import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Gift, Cookie as CookieIcon, Users, Calendar, Share2, PartyPopper } from "lucide-react";
import Layout from "../components/Layout";
import { useAuth } from "../context/AuthContext";
import { api } from "../lib/api";

const TIERS = [
  { icon: Calendar, label: "Birthday", value: "500 Points" },
  { icon: Users, label: "Referral", value: "50 Points" },
  { icon: CookieIcon, label: "$1 Spent", value: "15 Points" },
  { icon: Gift, label: "500 Points", value: "Free Cookie" },
];

export default function RewardsPage() {
  const { user, loading } = useAuth();
  const [data, setData] = useState(null);

  useEffect(() => {
    if (!user) return;
    api.get("/rewards/me").then(r => {
      setData(r.data);
      if (r.data.birthday_pts_just_awarded) {
        toast.success("🎂 Happy Birthday Week! +500 points just dropped in your account.", { duration: 6000 });
      }
    }).catch(() => {});
  }, [user]);

  const shareReferral = async () => {
    const code = data?.referral_code;
    if (!code) return;
    const text = `🍪 You need to try these cookies.\n\nFreshly baked every week:\nsugarysweeteverything.com\n\nUse my referral code ${code} and we both get rewards!`;
    if (navigator.share) {
      try { await navigator.share({ title: "Sugary Sweet", text }); return; } catch { /* user cancelled */ }
    }
    try { await navigator.clipboard.writeText(text); toast.success("Referral message copied!"); }
    catch { toast.error("Couldn't copy — long-press to select."); }
  };

  return (
    <Layout>
      <section className="page-section" data-testid="rewards-page">
        <div className="max-w-5xl mx-auto px-4 md:px-8">
          <div className="text-center mb-12">
            <p className="text-sm uppercase tracking-[0.25em] font-bold text-white/85 drop-shadow">Loyalty</p>
            <h1 className="font-display font-extrabold text-4xl md:text-6xl mt-2 text-white drop-shadow-lg">Sweet Rewards</h1>
            <p className="text-white/90 mt-4 max-w-xl mx-auto">Every cookie earns you points. Reach 500 points to redeem a free cookie.</p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-12">
            {TIERS.map(t => {
              const Icon = t.icon;
              return (
                <div key={t.label} className="card-soft p-5 text-center">
                  <div className="mx-auto w-12 h-12 rounded-2xl ss-gradient flex items-center justify-center mb-3">
                    <Icon className="w-6 h-6 text-white" />
                  </div>
                  <p className="font-display font-extrabold text-base">{t.label}</p>
                  <p className="text-sm text-[#FF1F87] font-bold mt-1">{t.value}</p>
                </div>
              );
            })}
          </div>

          {loading ? null : !user ? (
            <div className="card-soft p-8 text-center">
              <p className="text-[#4B5563] mb-4">Sign in to view your points and order history.</p>
              <Link to="/login" className="btn-pink" data-testid="rewards-login-link">LOG IN</Link>
            </div>
          ) : user.role === "admin" ? (
            <div className="card-soft p-8 text-center">
              <p className="text-[#4B5563]">Rewards are for customer accounts.</p>
            </div>
          ) : (
            <div className="card-soft p-6 md:p-8" data-testid="rewards-dashboard">
              {data?.is_birthday_week && (
                <div className="mb-6 flex items-center gap-3 p-4 rounded-2xl text-white ss-gradient" data-testid="birthday-banner">
                  <PartyPopper className="w-7 h-7 flex-shrink-0" />
                  <div>
                    <p className="font-display font-extrabold text-lg leading-tight">Happy Birthday Week, {user.full_name?.split(" ")[0]}! 🎂</p>
                    <p className="text-sm text-white/95 mt-0.5">We dropped <b>+500 points</b> in your account — redeem for a free cookie at checkout.</p>
                  </div>
                </div>
              )}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <p className="text-xs uppercase tracking-widest text-[#FF1F87] font-bold">Your Points</p>
                  <p className="font-display font-extrabold text-5xl md:text-6xl mt-1 text-gradient" data-testid="rewards-points">{data?.points ?? 0}</p>
                  <div className="mt-4 w-full h-3 bg-[#FFE4EE] rounded-full overflow-hidden">
                    <div className="h-full ss-gradient" style={{ width: `${Math.min(100, ((data?.points ?? 0) / 500) * 100)}%` }} />
                  </div>
                  <p className="text-xs text-[#4B5563] mt-2">{Math.max(0, 500 - (data?.points ?? 0))} points until your next free cookie.</p>
                </div>
                <div>
                  <p className="text-xs uppercase tracking-widest text-[#FF1F87] font-bold">Your Referral Code</p>
                  <div className="mt-2 inline-block ss-gradient text-white font-display font-extrabold px-5 py-3 rounded-2xl text-xl tracking-widest" data-testid="referral-code">{data?.referral_code || "—"}</div>
                  <p className="text-xs text-[#4B5563] mt-2">Share to earn 50 points per friend.</p>
                  <button
                    type="button"
                    onClick={shareReferral}
                    disabled={!data?.referral_code}
                    className="mt-3 inline-flex items-center gap-2 px-4 py-2 rounded-full border-2 border-[#FF1F87] text-[#FF1F87] font-bold text-sm uppercase tracking-wider hover:bg-[#FF1F87] hover:text-white transition disabled:opacity-50"
                    data-testid="share-referral-btn"
                  >
                    <Share2 className="w-4 h-4" /> Share Code
                  </button>
                </div>
              </div>

              <div className="mt-8">
                <h3 className="font-display font-extrabold text-xl mb-3">Order History</h3>
                {!data?.orders?.length ? (
                  <p className="text-sm text-[#4B5563]">No paid orders yet. <Link className="text-[#FF1F87] font-bold" to="/order">Order now</Link>.</p>
                ) : (
                  <ul className="divide-y divide-gray-100">
                    {data.orders.map(o => (
                      <li key={o.id} className="py-3 flex items-center justify-between text-sm" data-testid={`order-history-${o.id}`}>
                        <div>
                          <p className="font-bold">{o.order_number}</p>
                          <p className="text-[#4B5563] text-xs">{o.box_size}-pack · {o.method}</p>
                        </div>
                        <div className="text-right">
                          <p className="font-extrabold">${o.total.toFixed(2)}</p>
                          <p className="text-xs uppercase tracking-wider text-[#FF1F87]">{o.status}</p>
                        </div>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </div>
          )}
        </div>
      </section>
    </Layout>
  );
}
